import { cloneElement, ReactNode, useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import type { IntlFormatters } from 'react-intl'
import styled from 'styled-components'

import {
  AccordionCollapse,
  AccordionItem,
  AccordionToggle,
} from '@awazevr/design-system.components.accordion'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { Button } from '@awazevr/design-system.components.button'
import type { FilterProps } from '@awazevr/design-system.components.filter'
import {
  Input,
  InputControl,
  InputValidationMessage,
  Label,
} from '@awazevr/design-system.components.input'
import { Stack } from '@awazevr/design-system.components.stack'
import {
  Stepper,
  type StepperProps,
} from '@awazevr/design-system.components.stepper'
import { Text } from '@awazevr/design-system.components.text'
import { FilterList } from '@awazevr/design-system.compositions.filter-list'
import { breakpoints, pxToRems } from '@awazevr/design-system.utils.react'
import {
  Dispatch,
  FILTER_BATHROOM_TOGGLE,
  FILTER_BEDROOM_TOGGLE,
  FILTER_GRADE_TOGGLE,
  FILTER_RATING_TOGGLE,
  ONLY_DISCOUNTED_TOGGLE,
} from '@awazevr/mfe-common.contexts.search'
import type { TmsContextType } from '@awazevr/mfe-common.contexts.tms'
import type { AggregationFeatures } from '@awazevr/mfe-common.types-and-mocks.one-search-client'

import { VariantFilterAccordion } from '@/__experimental__/AS_2680_Filter_Accordion'
import type { OptimizelyDecisionsProps } from '@/context/optimizely-decisions-context'
import { isNovasolPoweredBrand } from '@/utils/isNovasolPoweredBrand'

import { fireFacetToggled } from './FilterActions/fireFacetEvents'
import {
  handleFilterClick,
  handleMappedFilterClick,
  handleMinMaxPriceFilterKeyPress,
  handlePriceFilterSubmit,
} from './FilterActions'
import {
  ClickedFeature,
  FeatureGroupIds,
  PriceFilterFormValues,
  ProcessedFeature,
} from './Filters.types'
import {
  bathroomMapper,
  bathroomUnmapper,
  bedroomMapper,
  bedroomUnmapper,
  getOrderedFeatures,
  GroupedFilterCategory,
  handleDLEventOnAccordionClick,
  isAccommodationFeature,
  isSiteFeature,
  setFeatureCountsToZero,
} from './Filters.utils'

import styles from './Filters.module.scss'

const PriceSubmitButton = styled(Button)`
  margin-bottom: 0.5rem !important;
`

const FILTER_STEPPER_MINIMUM = 0
const FILTER_STEPPER_MAXIMUM = 6
const FILTER_STEPPER_COMMIT_DELAY_MS = 500

type MappedFilterDispatchType =
  | typeof FILTER_BEDROOM_TOGGLE
  | typeof FILTER_BATHROOM_TOGGLE

const getSelectedStepperValue = (
  selectedFilters: string[],
  unmapper: (input: string) => number | null,
): number => {
  const selectedFilter = selectedFilters[0]
  const selectedValue = selectedFilter ? unmapper(selectedFilter) : null

  if (!selectedValue) {
    return FILTER_STEPPER_MINIMUM
  }

  return Math.min(
    Math.max(selectedValue, FILTER_STEPPER_MINIMUM),
    FILTER_STEPPER_MAXIMUM,
  )
}

const getMappedStepperFilterId = (
  value: number,
  mapper: (input: string | number) => string | number,
): string => String(mapper(value))

const buildAppliedStepperFilters = (
  value: number,
  mapper: (input: string | number) => string | number,
): string[] =>
  value > FILTER_STEPPER_MINIMUM
    ? [getMappedStepperFilterId(value, mapper)]
    : []

const getStepperEventFeatures = (
  allFeatures: FilterProps[],
  featureId: string,
): FilterProps[] => {
  const hasFeature = allFeatures.some(
    ({ featureId: currentFeatureId }) => String(currentFeatureId) === featureId,
  )

  if (hasFeature) {
    return allFeatures
  }

  return [
    ...allFeatures,
    {
      count: 0,
      featureId,
      featureName: featureId,
    },
  ]
}

interface CommitMappedStepperValueProps {
  allFeatures: FilterProps[]
  appliedValue: number
  dispatch: Dispatch
  dispatchType: MappedFilterDispatchType
  featureGroupId: FeatureGroupIds
  mapper: (input: string | number) => string | number
  nextValue: number
  optiContext: OptimizelyDecisionsProps
  tms: TmsContextType
}

const commitMappedStepperValue = ({
  allFeatures,
  appliedValue,
  dispatch,
  dispatchType,
  featureGroupId,
  mapper,
  nextValue,
  optiContext,
  tms,
}: CommitMappedStepperValueProps): void => {
  if (appliedValue === nextValue) {
    return
  }

  const trackedFeatureId = getMappedStepperFilterId(
    nextValue > FILTER_STEPPER_MINIMUM ? nextValue : appliedValue,
    mapper,
  )

  fireFacetToggled({
    allFeatures: getStepperEventFeatures(allFeatures, trackedFeatureId),
    appliedFeatures: buildAppliedStepperFilters(appliedValue, mapper),
    clickedFeature: {
      featureGroupId,
      featureId: trackedFeatureId,
    },
    tms,
    optiContext,
  })

  if (appliedValue > FILTER_STEPPER_MINIMUM) {
    dispatch({
      type: dispatchType,
      id: getMappedStepperFilterId(appliedValue, mapper),
    })
  }

  if (nextValue > FILTER_STEPPER_MINIMUM) {
    dispatch({
      type: dispatchType,
      id: getMappedStepperFilterId(nextValue, mapper),
    })
  }
}

interface BaseAccordionProps {
  accordionKey: string
  children: ReactNode
  open?: boolean
  title: ReactNode
  tms: TmsContextType
}

const FilterListCollapseContent = styled.div`
  padding-bottom: 12px;

  @media (min-width: ${breakpoints[1]}px) {
    padding-bottom: 0;
  }
`

const BaseAccordion = ({
  accordionKey,
  children,
  open = false,
  title,
  tms,
}: BaseAccordionProps): JSX.Element => (
  <AccordionItem
    open={open}
    accordionToggleCallback={(isOpen) =>
      handleDLEventOnAccordionClick(tms, isOpen, accordionKey)
    }
  >
    <AccordionToggle>{title}</AccordionToggle>
    <AccordionCollapse>{children}</AccordionCollapse>
  </AccordionItem>
)

interface FilterListAccordionProps {
  accordionKey: string
  ariaLabel: string
  description?: ReactNode
  filterGroup: string
  filters: FilterProps[]
  formatMessage: IntlFormatters['formatMessage']
  onFilterClick: (featureDetails: ClickedFeature) => void
  open?: boolean
  selectedFilters: Array<string | number>
  title: ReactNode
  tms: TmsContextType
  useVariantFilterAccordion?: boolean
}

const FilterListAccordion = ({
  accordionKey,
  ariaLabel,
  description,
  filterGroup,
  filters,
  formatMessage,
  onFilterClick,
  open = false,
  selectedFilters,
  title,
  tms,
  useVariantFilterAccordion = false,
}: FilterListAccordionProps): JSX.Element => {
  const hasFilters = filters.length > 0

  if (useVariantFilterAccordion) {
    return (
      <VariantFilterAccordion
        additionalAriaLabel={formatMessage(
          {
            id: 'filters.additional-list.aria-label',
            defaultMessage: '{ariaLabel} additional',
          },
          { ariaLabel },
        )}
        ariaLabel={ariaLabel}
        description={hasFilters ? description : undefined}
        filterGroup={filterGroup}
        filters={filters}
        formatShowMoreLabel={(remainingFilterCount) =>
          formatMessage(
            {
              id: 'filters.show-more-count',
              defaultMessage: 'Show {count} more',
            },
            { count: remainingFilterCount },
          )
        }
        onFilterClick={onFilterClick}
        onToggle={(isOpen) =>
          handleDLEventOnAccordionClick(tms, isOpen, accordionKey)
        }
        selectedFilters={selectedFilters}
        showLessLabel={formatMessage({
          id: 'filters.show-less',
          defaultMessage: 'Show less',
        })}
        title={title}
      />
    )
  }

  return (
    <BaseAccordion
      accordionKey={accordionKey}
      open={open}
      title={title}
      tms={tms}
    >
      {hasFilters && (
        <FilterListCollapseContent>
          {description}
          <FilterList
            filterGroup={filterGroup}
            filters={filters}
            selectedFilters={selectedFilters}
            aria-label={ariaLabel}
            onFilterClick={onFilterClick}
          />
        </FilterListCollapseContent>
      )}
    </BaseAccordion>
  )
}

interface MappedFilterStepperProps {
  allFeatures: FilterProps[]
  ariaLabel: string
  decreaseAriaLabel: string
  dispatch: Dispatch
  dispatchType: MappedFilterDispatchType
  featureGroupId: 'bedrooms' | 'bathrooms'
  filterGroup: string
  increaseAriaLabel: string
  mapper: (input: string | number) => string | number
  optiContext: OptimizelyDecisionsProps
  selectedFilters: string[]
  tms: TmsContextType
  unmapper: (input: string) => number | null
}

const MappedFilterStepper = ({
  allFeatures,
  ariaLabel,
  decreaseAriaLabel,
  dispatch,
  dispatchType,
  featureGroupId,
  filterGroup,
  increaseAriaLabel,
  mapper,
  optiContext,
  selectedFilters,
  tms,
  unmapper,
}: MappedFilterStepperProps): JSX.Element => {
  const selectedValue = getSelectedStepperValue(selectedFilters, unmapper)
  const [stepperValue, setStepperValue] = useState(selectedValue)
  const committedValueRef = useRef(selectedValue)
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current)
    }

    committedValueRef.current = selectedValue
    setStepperValue(selectedValue)
  }, [selectedValue])

  useEffect(
    () => () => {
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current)
      }
    },
    [],
  )

  const scheduleCommit = (nextValue: number | null) => {
    if (nextValue === null) {
      return
    }

    setStepperValue(nextValue)

    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current)
      commitTimeoutRef.current = null
    }

    if (nextValue === committedValueRef.current) {
      return
    }

    commitTimeoutRef.current = setTimeout(() => {
      const appliedValue = committedValueRef.current

      commitMappedStepperValue({
        allFeatures,
        appliedValue,
        dispatch,
        dispatchType,
        featureGroupId,
        mapper,
        nextValue,
        optiContext,
        tms,
      })

      committedValueRef.current = nextValue
      commitTimeoutRef.current = null
    }, FILTER_STEPPER_COMMIT_DELAY_MS)
  }

  return (
    <StepperWrapper isEmpty={stepperValue === FILTER_STEPPER_MINIMUM}>
      <FilterStepper
        key={`${filterGroup}-${selectedValue}`}
        name={`${filterGroup}-stepper`}
        aria-label={ariaLabel}
        initialValue={selectedValue}
        minimum={FILTER_STEPPER_MINIMUM}
        maximum={FILTER_STEPPER_MAXIMUM}
        rounded={false}
        translations={{
          fullDecreaseAriaLabel: decreaseAriaLabel,
          fullIncreaseAriaLabel: increaseAriaLabel,
        }}
        callBack={scheduleCommit}
        colourScheme="brand-primary"
      />
    </StepperWrapper>
  )
}

type FilterStepperProps = StepperProps & {
  'aria-label': string
}

const FilterStepper = styled(Stepper)<FilterStepperProps>`
  button {
    border-radius: 4px;
    height: 40px;
    width: 40px;
  }
`

interface BedroomsBathroomsAccordionProps
  extends MappedFilterAccordionCommonProps {
  bathroomOptions: FilterProps[]
  bedroomOptions: FilterProps[]
  loading: boolean
  selectedBathrooms: string[]
  selectedBedrooms: string[]
}

export const BedroomsBathroomsAccordion = ({
  bathroomOptions,
  bedroomOptions,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  loading,
  optiContext,
  selectedBathrooms,
  selectedBedrooms,
  tms,
  useVariantFilterAccordion = false,
}: BedroomsBathroomsAccordionProps): JSX.Element | null => {
  const hasBedroomOrBathroomOptions =
    bedroomOptions.length > 0 || bathroomOptions.length > 0
  const title = formatMessage({
    id: 'filters.bedrooms-bathrooms.heading',
    defaultMessage: 'Bedrooms and bathrooms',
  })

  if (!loading && !hasBedroomOrBathroomOptions) {
    return null
  }

  const hasBedroomOptions = bedroomOptions.length > 0
  const hasBathroomOptions = bathroomOptions.length > 0

  if (loading && !hasBedroomOrBathroomOptions) {
    if (useVariantFilterAccordion) {
      return (
        <VariantStepperAccordionContainer data-testid="variant-filter-stepper-accordion">
          <VariantStepperAccordionTitle>{title}</VariantStepperAccordionTitle>
        </VariantStepperAccordionContainer>
      )
    }

    return (
      <BaseAccordion
        accordionKey="bedrooms"
        open={false}
        title={title}
        tms={tms}
      >
        {null}
      </BaseAccordion>
    )
  }

  const bedroomsLabel = formatMessage({
    id: 'filters.bedrooms.heading',
    defaultMessage: 'Bedrooms',
  })
  const bathroomsLabel = formatMessage({
    id: 'filters.bathrooms.heading',
    defaultMessage: 'Bathrooms',
  })
  const decreaseBedroomsLabel = formatMessage({
    id: 'filters.bedrooms.decrease-aria-label',
    defaultMessage: 'Decrease Bedrooms',
  })
  const increaseBedroomsLabel = formatMessage({
    id: 'filters.bedrooms.increase-aria-label',
    defaultMessage: 'Increase Bedrooms',
  })
  const decreaseBathroomsLabel = formatMessage({
    id: 'filters.bathrooms.decrease-aria-label',
    defaultMessage: 'Decrease Bathrooms',
  })
  const increaseBathroomsLabel = formatMessage({
    id: 'filters.bathrooms.increase-aria-label',
    defaultMessage: 'Increase Bathrooms',
  })
  const minimumOfLabel = formatMessage({
    id: 'filters.bedrooms-bathrooms.minimum-of',
    defaultMessage: 'Minimum of',
  })
  const content = (
    <CombinedStepperList>
      {hasBedroomOptions && (
        <CombinedStepperOption>
          <CombinedStepperText>
            <CombinedStepperLabel>{bedroomsLabel}</CombinedStepperLabel>
            <CombinedStepperSubtitle>{minimumOfLabel}</CombinedStepperSubtitle>
          </CombinedStepperText>
          <MappedFilterStepper
            allFeatures={bedroomOptions}
            ariaLabel={bedroomsLabel}
            decreaseAriaLabel={decreaseBedroomsLabel}
            dispatch={dispatch}
            dispatchType={FILTER_BEDROOM_TOGGLE}
            featureGroupId="bedrooms"
            filterGroup={`bedrooms-${filterGroupIdSuffix}`}
            increaseAriaLabel={increaseBedroomsLabel}
            mapper={bedroomMapper}
            optiContext={optiContext}
            selectedFilters={selectedBedrooms}
            tms={tms}
            unmapper={bedroomUnmapper}
          />
        </CombinedStepperOption>
      )}
      {hasBathroomOptions && (
        <CombinedStepperOption>
          <CombinedStepperText>
            <CombinedStepperLabel>{bathroomsLabel}</CombinedStepperLabel>
            <CombinedStepperSubtitle>{minimumOfLabel}</CombinedStepperSubtitle>
          </CombinedStepperText>
          <MappedFilterStepper
            allFeatures={bathroomOptions}
            ariaLabel={bathroomsLabel}
            decreaseAriaLabel={decreaseBathroomsLabel}
            dispatch={dispatch}
            dispatchType={FILTER_BATHROOM_TOGGLE}
            featureGroupId="bathrooms"
            filterGroup={`bathrooms-${filterGroupIdSuffix}`}
            increaseAriaLabel={increaseBathroomsLabel}
            mapper={bathroomMapper}
            optiContext={optiContext}
            selectedFilters={selectedBathrooms}
            tms={tms}
            unmapper={bathroomUnmapper}
          />
        </CombinedStepperOption>
      )}
    </CombinedStepperList>
  )

  if (useVariantFilterAccordion) {
    return (
      <VariantStepperAccordionContainer data-testid="variant-filter-stepper-accordion">
        <VariantStepperAccordionTitle>{title}</VariantStepperAccordionTitle>
        {content}
      </VariantStepperAccordionContainer>
    )
  }

  return (
    <BaseAccordion accordionKey="bedrooms" open={false} title={title} tms={tms}>
      {content}
    </BaseAccordion>
  )
}

const CombinedStepperList = styled.div`
  display: flex;
  flex-direction: column;
`

const CombinedStepperOption = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

const CombinedStepperLabel = styled.span`
  color: ${({ theme }) => theme.ColorGrey800};
  font-size: ${({ theme }) => pxToRems(theme.FontTextText200FontSize)};
`

const CombinedStepperText = styled.div`
  display: flex;
  flex-direction: column;
`

const CombinedStepperSubtitle = styled.span`
  color: ${({ theme }) => theme.ColorTextBodyOnLight};
  font-size: ${({ theme }) => pxToRems(theme.FontTextText100FontSize)};
  line-height: ${({ theme }) => pxToRems(theme.FontTextText100LineHeight)};
`

const VariantStepperAccordionContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.ColorGrey100};
  color: ${({ theme }) => theme.ColorGrey800};
  padding-bottom: 12px;

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.ColorGrey100};
  }
`

const VariantStepperAccordionTitle = styled.h3`
  font-size: ${({ theme }) => pxToRems(theme.FontTextText300MidFontSize)};
  font-weight: ${({ theme }) => theme.FontTextText400MidFontWeight};
  line-height: ${({ theme }) => pxToRems(theme.FontTextText300MidLineHeight)};
  margin: 0;
  padding: 12px 0;
`

const StepperWrapper = styled.div<{ isEmpty: boolean }>`
  display: flex;
  justify-content: flex-start;
  padding: 0;

  input {
    color: ${({ isEmpty, theme }) =>
      isEmpty ? theme.ColorGrey500 : theme.ColorGrey800};
  }
`

interface FeatureAccordionCommonProps {
  accommodationTypesAsFeatures: ProcessedFeature[]
  allFeatures: ProcessedFeature[]
  dispatch: Dispatch
  filterGroupIdSuffix: string
  formatMessage: IntlFormatters['formatMessage']
  optiContext: OptimizelyDecisionsProps
  selectedFilters: string[]
  siteFeatures: AggregationFeatures[]
  tms: TmsContextType
  useVariantFilterAccordion?: boolean
}

interface PopularFeaturesAccordionProps extends FeatureAccordionCommonProps {
  isInAlphabeticalFiltersExp: boolean
  loading: boolean
  popularFeatures: ProcessedFeature[]
  shouldShowZeroFeaturesCount: boolean
}

export const PopularFeaturesAccordion = ({
  accommodationTypesAsFeatures,
  allFeatures,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  isInAlphabeticalFiltersExp,
  loading,
  optiContext,
  popularFeatures,
  selectedFilters,
  shouldShowZeroFeaturesCount,
  siteFeatures,
  tms,
  useVariantFilterAccordion = false,
}: PopularFeaturesAccordionProps): JSX.Element | null => {
  if (!(loading || popularFeatures.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="popularFeatures"
      ariaLabel={formatMessage({
        id: 'filters.popular-features.aria-label',
        defaultMessage: 'Popular features list',
      })}
      filterGroup={`popularFeatures-${filterGroupIdSuffix}`}
      filters={getOrderedFeatures(
        popularFeatures,
        isInAlphabeticalFiltersExp,
        shouldShowZeroFeaturesCount,
      )}
      formatMessage={formatMessage}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleFilterClick({
          allFeatures,
          appliedFeatures: selectedFilters,
          dispatch,
          clickedFeature: {
            featureGroupId: 'popularFeatures',
            isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
            isAccommodationFeature: isAccommodationFeature(
              featureDetails,
              accommodationTypesAsFeatures,
            ),
            ...featureDetails,
          },
          tms,
          optiContext,
        })
      }}
      open={true}
      selectedFilters={selectedFilters}
      title={formatMessage({
        id: 'filters.popular-features.heading',
        defaultMessage: 'Popular features',
      })}
      tms={tms}
      useVariantFilterAccordion={useVariantFilterAccordion}
    />
  )
}

interface PopularDestinationsAccordionProps
  extends FeatureAccordionCommonProps {
  brand: Brand
  isInAlphabeticalFiltersExp: boolean
  loading: boolean
  popularDestinations: ProcessedFeature[]
  shouldShowZeroFeaturesCount: boolean
  showPopularDestinationFiltersExp: boolean
}

export const PopularDestinationsAccordion = ({
  allFeatures,
  brand,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  isInAlphabeticalFiltersExp,
  loading,
  optiContext,
  popularDestinations,
  selectedFilters,
  shouldShowZeroFeaturesCount,
  showPopularDestinationFiltersExp,
  siteFeatures,
  tms,
  useVariantFilterAccordion = false,
}: PopularDestinationsAccordionProps): JSX.Element | null => {
  if (
    !showPopularDestinationFiltersExp ||
    ((isNovasolPoweredBrand(brand) || !loading) &&
      popularDestinations.length === 0)
  ) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="popularDestinations"
      ariaLabel={formatMessage({
        id: 'filters.popular-destinations-list.aria-label',
        defaultMessage: 'Popular destinations list',
      })}
      filterGroup={`popularDestinations-${filterGroupIdSuffix}`}
      filters={getOrderedFeatures(
        popularDestinations,
        isInAlphabeticalFiltersExp,
        shouldShowZeroFeaturesCount,
      )}
      formatMessage={formatMessage}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleFilterClick({
          allFeatures,
          appliedFeatures: selectedFilters,
          dispatch,
          clickedFeature: {
            featureGroupId: 'popularDestinations',
            isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
            ...featureDetails,
          },
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedFilters}
      title={formatMessage({
        id: 'filters.popular-destinations',
        defaultMessage: 'Popular destinations',
      })}
      tms={tms}
      useVariantFilterAccordion={useVariantFilterAccordion}
    />
  )
}

interface GroupedFilterAccordionProps extends FeatureAccordionCommonProps {
  group: GroupedFilterCategory
}

export const GroupedFilterAccordion = ({
  accommodationTypesAsFeatures,
  allFeatures,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  group,
  optiContext,
  selectedFilters,
  siteFeatures,
  tms,
  useVariantFilterAccordion = false,
}: GroupedFilterAccordionProps): JSX.Element | null => {
  return (
    <FilterListAccordion
      accordionKey={group.key}
      ariaLabel={formatMessage(
        {
          id: 'filters.grouped-list.aria-label',
          defaultMessage: '{groupName} list',
        },
        { groupName: group.groupName },
      )}
      filterGroup={`${group.key}-${filterGroupIdSuffix}`}
      filters={group.filters}
      formatMessage={formatMessage}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleFilterClick({
          allFeatures,
          appliedFeatures: selectedFilters,
          dispatch,
          clickedFeature: {
            ...featureDetails,
            isAccommodationFeature: isAccommodationFeature(
              featureDetails,
              accommodationTypesAsFeatures,
            ),
            isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
            featureGroupId: group.key,
          },
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedFilters}
      title={group.groupName}
      tms={tms}
      useVariantFilterAccordion={useVariantFilterAccordion}
    />
  )
}

interface AllFeaturesAccordionProps extends FeatureAccordionCommonProps {
  allVisibleFeatures: ProcessedFeature[]
  isInAlphabeticalFiltersExp: boolean
  loading: boolean
  shouldShowZeroFeaturesCount: boolean
}

export const AllFeaturesAccordion = ({
  accommodationTypesAsFeatures,
  allFeatures,
  allVisibleFeatures,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  isInAlphabeticalFiltersExp,
  loading,
  optiContext,
  selectedFilters,
  shouldShowZeroFeaturesCount,
  siteFeatures,
  tms,
  useVariantFilterAccordion = false,
}: AllFeaturesAccordionProps): JSX.Element | null => {
  if (!(loading || allVisibleFeatures.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="allFeatures"
      ariaLabel={formatMessage({
        id: 'filters.all-features.aria-label',
        defaultMessage: 'All features list',
      })}
      filterGroup={`allFeatures-${filterGroupIdSuffix}`}
      filters={getOrderedFeatures(
        allVisibleFeatures,
        isInAlphabeticalFiltersExp,
        shouldShowZeroFeaturesCount,
      )}
      formatMessage={formatMessage}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleFilterClick({
          allFeatures,
          appliedFeatures: selectedFilters,
          dispatch,
          clickedFeature: {
            featureGroupId: 'allFeatures',
            isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
            isAccommodationFeature: isAccommodationFeature(
              featureDetails,
              accommodationTypesAsFeatures,
            ),
            ...featureDetails,
          },
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedFilters}
      title={formatMessage({
        id: 'filters.all-features.heading',
        defaultMessage: 'All features',
      })}
      tms={tms}
      useVariantFilterAccordion={useVariantFilterAccordion}
    />
  )
}

interface MappedFilterAccordionCommonProps {
  dispatch: Dispatch
  filterGroupIdSuffix: string
  formatMessage: IntlFormatters['formatMessage']
  optiContext: OptimizelyDecisionsProps
  shouldShowZeroFeaturesCount: boolean
  tms: TmsContextType
  useVariantFilterAccordion?: boolean
}

interface BedroomsAccordionProps extends MappedFilterAccordionCommonProps {
  bedroomOptions: FilterProps[]
  loading: boolean
  selectedBedrooms: string[]
}

export const BedroomsAccordion = ({
  bedroomOptions,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  loading,
  optiContext,
  selectedBedrooms,
  shouldShowZeroFeaturesCount,
  tms,
  useVariantFilterAccordion = false,
}: BedroomsAccordionProps): JSX.Element | null => {
  if (!(loading || bedroomOptions.length > 0)) {
    return null
  }

  const title = formatMessage({
    id: 'filters.bedrooms.heading',
    defaultMessage: 'Bedrooms',
  })
  const ariaLabel = formatMessage({
    id: 'filters.bedrooms.aria-label',
    defaultMessage: 'Bedrooms',
  })

  return (
    <FilterListAccordion
      accordionKey="bedrooms"
      ariaLabel={ariaLabel}
      filterGroup={`bedrooms-${filterGroupIdSuffix}`}
      filters={
        shouldShowZeroFeaturesCount
          ? setFeatureCountsToZero(bedroomOptions)
          : bedroomOptions
      }
      formatMessage={formatMessage}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleMappedFilterClick({
          allFeatures: bedroomOptions,
          appliedFeatures: selectedBedrooms,
          clickedFeature: {
            featureGroupId: 'bedrooms',
            ...featureDetails,
          },
          dispatch,
          dispatchType: FILTER_BEDROOM_TOGGLE,
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedBedrooms}
      title={title}
      tms={tms}
      useVariantFilterAccordion={useVariantFilterAccordion}
    />
  )
}

interface BathroomsAccordionProps extends MappedFilterAccordionCommonProps {
  bathroomOptions: FilterProps[]
  loading: boolean
  selectedBathrooms: string[]
}

export const BathroomsAccordion = ({
  bathroomOptions,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  loading,
  optiContext,
  selectedBathrooms,
  shouldShowZeroFeaturesCount,
  tms,
  useVariantFilterAccordion = false,
}: BathroomsAccordionProps): JSX.Element | null => {
  if (!(loading || bathroomOptions.length > 0)) {
    return null
  }

  const title = formatMessage({
    id: 'filters.bathrooms.heading',
    defaultMessage: 'Bathrooms',
  })
  const ariaLabel = formatMessage({
    id: 'filters.bathrooms.aria-label',
    defaultMessage: 'Bathrooms',
  })

  return (
    <FilterListAccordion
      accordionKey="bathrooms"
      ariaLabel={ariaLabel}
      filterGroup={`bathrooms-${filterGroupIdSuffix}`}
      filters={
        shouldShowZeroFeaturesCount
          ? setFeatureCountsToZero(bathroomOptions)
          : bathroomOptions
      }
      formatMessage={formatMessage}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleMappedFilterClick({
          allFeatures: bathroomOptions,
          appliedFeatures: selectedBathrooms,
          clickedFeature: {
            featureGroupId: 'bathrooms',
            ...featureDetails,
          },
          dispatch,
          dispatchType: FILTER_BATHROOM_TOGGLE,
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedBathrooms}
      title={title}
      tms={tms}
      useVariantFilterAccordion={useVariantFilterAccordion}
    />
  )
}

interface StarRatingAccordionProps extends MappedFilterAccordionCommonProps {
  displayGradeOptions: FilterProps[]
  gradeOptions: FilterProps[]
  loading: boolean
  selectedGrades: string[]
}

export const StarRatingAccordion = ({
  dispatch,
  displayGradeOptions,
  filterGroupIdSuffix,
  formatMessage,
  gradeOptions,
  loading,
  optiContext,
  selectedGrades,
  shouldShowZeroFeaturesCount,
  tms,
  useVariantFilterAccordion = false,
}: StarRatingAccordionProps): JSX.Element | null => {
  if (!(loading || gradeOptions.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="starRating"
      ariaLabel={formatMessage({
        id: 'filters.star-rating.heading',
        defaultMessage: 'Star rating',
      })}
      description={
        <Text sizes={100} mb={12}>
          {formatMessage({
            id: 'filters.star-rating.explanation',
            defaultMessage:
              'To indicate quality, the more stars, the more comfort you can expect from the property.',
          })}
        </Text>
      }
      filterGroup={`grades-${filterGroupIdSuffix}`}
      filters={
        shouldShowZeroFeaturesCount
          ? setFeatureCountsToZero(displayGradeOptions)
          : displayGradeOptions
      }
      formatMessage={formatMessage}
      onFilterClick={({ featureId }) => {
        handleMappedFilterClick({
          allFeatures: gradeOptions,
          appliedFeatures: selectedGrades,
          clickedFeature: {
            featureId,
            featureGroupId: 'starRating',
          },
          dispatch,
          dispatchType: FILTER_GRADE_TOGGLE,
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedGrades}
      title={formatMessage({
        id: 'filters.star-rating.heading',
        defaultMessage: 'Star rating',
      })}
      tms={tms}
      useVariantFilterAccordion={useVariantFilterAccordion}
    />
  )
}

interface CustomerReviewAccordionProps
  extends MappedFilterAccordionCommonProps {
  loading: boolean
  reviewOptions: FilterProps[]
  selectedRatings: string[]
}

export const CustomerReviewAccordion = ({
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  loading,
  optiContext,
  reviewOptions,
  selectedRatings,
  shouldShowZeroFeaturesCount,
  tms,
  useVariantFilterAccordion = false,
}: CustomerReviewAccordionProps): JSX.Element | null => {
  if (!(loading || reviewOptions.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="customerReviewScore"
      ariaLabel={formatMessage({
        id: 'filters.customer-review.aria-label',
        defaultMessage: 'Customer review score',
      })}
      filterGroup={`reviews-${filterGroupIdSuffix}`}
      filters={
        shouldShowZeroFeaturesCount
          ? setFeatureCountsToZero(reviewOptions)
          : reviewOptions
      }
      formatMessage={formatMessage}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleMappedFilterClick({
          allFeatures: reviewOptions,
          appliedFeatures: selectedRatings,
          clickedFeature: {
            featureGroupId: 'customerReviewScore',
            ...featureDetails,
          },
          dispatch,
          dispatchType: FILTER_RATING_TOGGLE,
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedRatings}
      title={formatMessage({
        id: 'filters.customer-review.heading',
        defaultMessage: 'Customer review score',
      })}
      tms={tms}
      useVariantFilterAccordion={useVariantFilterAccordion}
    />
  )
}

interface PriceRangeAccordionProps {
  alwaysExpanded?: boolean
  currencySymbol: string
  dispatch: Dispatch
  formatMessage: IntlFormatters['formatMessage']
  formHooks: UseFormReturn<PriceFilterFormValues>
  hideToggle?: boolean
  priceRangeError: boolean
  setPriceRangeError: React.Dispatch<React.SetStateAction<boolean>>
  showPriceFilter: string
  tms: TmsContextType
}

export const PriceRangeAccordion = ({
  alwaysExpanded = false,
  currencySymbol,
  dispatch,
  formatMessage,
  formHooks,
  hideToggle = false,
  priceRangeError,
  setPriceRangeError,
  showPriceFilter,
  tms,
}: PriceRangeAccordionProps): JSX.Element | null => {
  if (!showPriceFilter) {
    return null
  }

  const title = formatMessage(
    {
      id: 'filters.price-range.heading',
      defaultMessage: 'Price Range ({currency})',
    },
    { currency: currencySymbol },
  )
  const content = (
    <PriceRangeContent
      dispatch={dispatch}
      formatMessage={formatMessage}
      formHooks={formHooks}
      priceRangeError={priceRangeError}
      setPriceRangeError={setPriceRangeError}
      tms={tms}
    />
  )

  if (hideToggle) {
    return (
      <AccordionItem
        key={
          alwaysExpanded
            ? 'price-range-static-open'
            : 'price-range-static-closed'
        }
        open={alwaysExpanded}
      >
        <StaticAccordionTitle>{title}</StaticAccordionTitle>
        <AccordionCollapse>{content}</AccordionCollapse>
      </AccordionItem>
    )
  }

  return (
    <BaseAccordion accordionKey="price-range" title={title} tms={tms}>
      {content}
    </BaseAccordion>
  )
}

interface PriceRangeContentProps {
  dispatch: Dispatch
  formatMessage: IntlFormatters['formatMessage']
  formHooks: UseFormReturn<PriceFilterFormValues>
  priceRangeError: boolean
  setPriceRangeError: React.Dispatch<React.SetStateAction<boolean>>
  tms: TmsContextType
}

const PriceRangeContent = ({
  dispatch,
  formatMessage,
  formHooks,
  priceRangeError,
  setPriceRangeError,
  tms,
}: PriceRangeContentProps): JSX.Element => (
  <>
    <Text sizes={100} mb={12}>
      {formatMessage({
        id: 'filters.price-range.explanation',
        defaultMessage:
          'Enter the minimum and maximum price for the total duration of your stay',
      })}
    </Text>
    <form
      onSubmit={formHooks.handleSubmit((data) =>
        handlePriceFilterSubmit(data, setPriceRangeError, dispatch, tms),
      )}
      noValidate
    >
      <Stack isHorizontal align="flex-end">
        <InputControl>
          <Label htmlFor="minPrice">
            {formatMessage({
              id: 'filters.price-range.min',
              defaultMessage: 'Min',
            })}
          </Label>
          <Input
            name="minPrice"
            aria-label={formatMessage({
              id: 'filters.price-range.aria-min',
              defaultMessage: 'Minimum Price',
            })}
            type="number"
            formHooks={formHooks}
            onKeyDown={handleMinMaxPriceFilterKeyPress}
            status={priceRangeError ? 'error' : 'default'}
          />
        </InputControl>
        <InputControl>
          <Label htmlFor="maxPrice">
            {formatMessage({
              id: 'filters.price-range.max',
              defaultMessage: 'Max',
            })}
          </Label>
          <Input
            name="maxPrice"
            aria-label={formatMessage({
              id: 'filters.price-range.aria-max',
              defaultMessage: 'Maximum Price',
            })}
            type="number"
            formHooks={formHooks}
            onKeyDown={handleMinMaxPriceFilterKeyPress}
            status={priceRangeError ? 'error' : 'default'}
          />
        </InputControl>
        <PriceSubmitButton
          text={formatMessage({
            id: 'filters.price-range.submit',
            defaultMessage: 'Apply',
          })}
          type="submit"
        />
      </Stack>
      {priceRangeError && (
        <InputValidationMessage status="error">
          <Text sizes={100}>
            {formatMessage({
              id: 'filters.price-range.validation-message',
              defaultMessage: "Min value can't be greater than the Max value",
            })}
          </Text>
        </InputValidationMessage>
      )}
    </form>
  </>
)

const StaticAccordionTitle = styled.div`
  align-items: center;
  display: flex;
  font-size: ${({ theme }) => pxToRems(theme.FontTextText300MidFontSize)};
  font-weight: ${({ theme }) => theme.FontTextText400MidFontWeight};
  justify-content: space-between;
  line-height: ${({ theme }) => pxToRems(theme.FontTextText300MidLineHeight)};
  padding: 12px 0;
`

interface ExtrasAccordionProps {
  discountsSelected: boolean
  dispatch: Dispatch
  filterGroupIdSuffix: string
  formatMessage: IntlFormatters['formatMessage']
  optiContext: OptimizelyDecisionsProps
  tms: TmsContextType
}

export const ExtrasAccordion = ({
  discountsSelected,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  optiContext,
  tms,
}: ExtrasAccordionProps): JSX.Element => {
  const dealsFeature = {
    featureId: 'deals',
    featureName: formatMessage({
      id: 'filters.extras.deals',
      defaultMessage: 'Deals',
    }),
    count: 1,
  }

  return (
    <BaseAccordion
      accordionKey="extras"
      title={formatMessage({
        id: 'filters.extras.heading',
        defaultMessage: 'Extras',
      })}
      tms={tms}
    >
      <FilterList
        filterGroup={`extras-${filterGroupIdSuffix}`}
        filters={[dealsFeature]}
        selectedFilters={discountsSelected ? [dealsFeature.featureId] : []}
        aria-label={formatMessage({
          id: 'filters.extras.aria-label',
          defaultMessage: 'Extras',
        })}
        onFilterClick={() => {
          fireFacetToggled({
            allFeatures: [dealsFeature],
            appliedFeatures: discountsSelected ? [dealsFeature.featureId] : [],
            clickedFeature: {
              featureId: dealsFeature.featureId,
              featureGroupId: 'extras',
            },
            tms,
            optiContext,
          })
          dispatch({ type: ONLY_DISCOUNTED_TOGGLE })
        }}
      />
    </BaseAccordion>
  )
}

interface SalesAndPromotionsAccordionProps extends FeatureAccordionCommonProps {
  discountsSelected: boolean
  group: GroupedFilterCategory
}

export const SalesAndPromotionsAccordion = ({
  accommodationTypesAsFeatures,
  allFeatures,
  discountsSelected,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  group,
  optiContext,
  selectedFilters,
  siteFeatures,
  tms,
  useVariantFilterAccordion = false,
}: SalesAndPromotionsAccordionProps): JSX.Element => {
  const dealsFeature = {
    featureId: 'deals',
    featureName: formatMessage({
      id: 'filters.extras.deals',
      defaultMessage: 'Deals',
    }),
    count: 1,
  }

  const filters = [...group.filters, dealsFeature]
  const selectedSalesAndPromotionsFilters = discountsSelected
    ? [...selectedFilters, dealsFeature.featureId]
    : selectedFilters
  const ariaLabel = formatMessage(
    {
      id: 'filters.grouped-list.aria-label',
      defaultMessage: '{groupName} list',
    },
    { groupName: group.groupName },
  )
  const onFilterClick = (featureDetails: ClickedFeature) => {
    if (featureDetails.featureId === 'deals') {
      fireFacetToggled({
        allFeatures: [dealsFeature],
        appliedFeatures: discountsSelected ? [dealsFeature.featureId] : [],
        clickedFeature: {
          featureId: dealsFeature.featureId,
          featureGroupId: 'salesAndPromotions',
        },
        tms,
        optiContext,
      })
      dispatch({ type: ONLY_DISCOUNTED_TOGGLE })
      return
    }
    handleFilterClick({
      allFeatures,
      appliedFeatures: selectedFilters,
      dispatch,
      clickedFeature: {
        ...featureDetails,
        isAccommodationFeature: isAccommodationFeature(
          featureDetails,
          accommodationTypesAsFeatures,
        ),
        isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
        featureGroupId: group.key,
      },
      tms,
      optiContext,
    })
  }

  if (useVariantFilterAccordion) {
    return (
      <div className={styles.salesAndPromotionsFilter}>
        <VariantFilterAccordion
          additionalAriaLabel={formatMessage(
            {
              id: 'filters.additional-list.aria-label',
              defaultMessage: '{ariaLabel} additional',
            },
            { ariaLabel },
          )}
          ariaLabel={ariaLabel}
          filterGroup={`${group.key}-${filterGroupIdSuffix}`}
          filters={filters}
          formatShowMoreLabel={(remainingFilterCount) =>
            formatMessage(
              {
                id: 'filters.show-more-count',
                defaultMessage: 'Show {count} more',
              },
              { count: remainingFilterCount },
            )
          }
          onFilterClick={onFilterClick}
          onToggle={(isOpen) =>
            handleDLEventOnAccordionClick(tms, isOpen, group.key)
          }
          selectedFilters={selectedSalesAndPromotionsFilters}
          showLessLabel={formatMessage({
            id: 'filters.show-less',
            defaultMessage: 'Show less',
          })}
          title={group.groupName}
        />
      </div>
    )
  }

  return (
    <BaseAccordion accordionKey={group.key} title={group.groupName} tms={tms}>
      <div className={styles.salesAndPromotionsFilter}>
        <FilterList
          filterGroup={`${group.key}-${filterGroupIdSuffix}`}
          filters={filters}
          selectedFilters={selectedSalesAndPromotionsFilters}
          aria-label={ariaLabel}
          onFilterClick={onFilterClick}
        />
      </div>
    </BaseAccordion>
  )
}

export interface AccordionSection {
  element: JSX.Element | null
  key: string
  title: string
}

const pinnedAccordionKeys = ['popularFeatures']

export const getOrderedAccordionSections = (
  sections: AccordionSection[],
  {
    orderedKeys = [],
  }: {
    orderedKeys?: string[]
  } = {},
): Array<JSX.Element> => {
  const orderMap = new Map(orderedKeys.map((key, index) => [key, index]))
  const pinnedOrderMap = new Map(
    pinnedAccordionKeys.map((key, index) => [key, index]),
  )

  return sections
    .map((section, index) => ({ ...section, index }))
    .sort((firstSection, secondSection) => {
      const firstPinnedOrder = pinnedOrderMap.get(firstSection.key)
      const secondPinnedOrder = pinnedOrderMap.get(secondSection.key)

      if (firstPinnedOrder !== undefined || secondPinnedOrder !== undefined) {
        if (firstPinnedOrder === undefined) {
          return 1
        }

        if (secondPinnedOrder === undefined) {
          return -1
        }

        return firstPinnedOrder - secondPinnedOrder
      }

      const firstOrder = orderMap.get(firstSection.key)
      const secondOrder = orderMap.get(secondSection.key)

      if (firstOrder === undefined && secondOrder === undefined) {
        return firstSection.index - secondSection.index
      }

      if (firstOrder === undefined) {
        return 1
      }

      if (secondOrder === undefined) {
        return -1
      }

      return firstOrder - secondOrder
    })
    .flatMap(({ element, key }) =>
      element ? [cloneElement(element, { key })] : [],
    )
}
